import { ApiError } from './api';
import { formatBytes } from './format';
import type { I18n } from '../i18n';

/**
 * Turn whatever went wrong into a sentence with the actual numbers in it.
 *
 * A quota error has to say how many runs are left and when the counter resets;
 * a size-cap error has to say both the limit and the size that broke it.
 * Anything less makes the user guess, and the guess is usually wrong.
 */
export interface ErrorDescription {
  /** What happened, in the user's language, with the real numbers. */
  message: string;
  /** Present when the failure is transient and retrying is sensible. */
  retryable: boolean;
}

function instant(value: string | null, i18n: I18n): string {
  if (!value) return i18n.t('common.dash');
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(i18n.tag, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

export function describeError(error: unknown, i18n: I18n): ErrorDescription {
  const { t } = i18n;

  if (!(error instanceof ApiError)) {
    const message = error instanceof Error ? error.message : String(error);
    return { message: t('error.unknown', { message }), retryable: true };
  }

  const limit = error.num('limit');
  const actual = error.num('actual');

  switch (error.code) {
    case 'quota_exceeded':
      return {
        message: t('error.quota_exceeded', {
          limit: limit ?? '—',
          resets: instant(error.str('resets_at'), i18n),
        }),
        retryable: false,
      };

    case 'rate_limited':
      return {
        message: t('error.rate_limited', { seconds: error.num('retry_after') ?? 60 }),
        retryable: true,
      };

    case 'too_many_documents':
      return {
        message: t('error.too_many_documents', { actual: actual ?? '—', limit: limit ?? '—' }),
        retryable: false,
      };

    case 'too_many_items':
      return {
        message: t('error.too_many_items', { actual: actual ?? '—', limit: limit ?? '—' }),
        retryable: false,
      };

    case 'upload_too_large':
      return {
        message: t('error.upload_too_large', {
          actual: actual === null ? '—' : formatBytes(actual, i18n.tag),
          limit: limit === null ? '—' : formatBytes(limit, i18n.tag),
        }),
        retryable: false,
      };

    case 'parse_failed':
      return { message: t('error.parse_failed', { message: error.message }), retryable: false };

    case 'empty_result':
      return { message: t('error.empty_result'), retryable: false };

    case 'empty_map':
      return { message: t('error.empty_map'), retryable: false };

    case 'unauthenticated':
      return { message: t('error.unauthenticated'), retryable: false };

    case 'forbidden':
      return { message: t('error.forbidden'), retryable: false };

    case 'not_found':
      return { message: t('error.not_found'), retryable: false };

    case 'network':
      return { message: t('error.network'), retryable: true };

    case 'timeout':
      return { message: t('error.timeout'), retryable: true };

    case 'cancelled':
      return { message: t('error.cancelled'), retryable: true };

    // Both are raised by api.ts itself, before anything is sent: a control
    // the form offers that this API has no field for (`n_terms`, a raw-string
    // co-citation key) and an OpenAlex filter that cannot be expressed. They
    // carry their own full sentence, so this only has to stop framing a
    // client-side refusal as a server failure -- and stop offering a retry,
    // which cannot succeed until the user changes the setting.
    case 'unsupported_parameter':
    case 'unsupported_filter':
      return { message: t('error.unsupported', { message: error.message }), retryable: false };

    case 'not_configured':
      return {
        message: t('error.notConfigured', { name: error.str('name') ?? error.message }),
        retryable: false,
      };

    default:
      return { message: t('error.internal', { message: error.message }), retryable: true };
  }
}
